'use client';

import { useQuery } from '@tanstack/react-query';
import { TripItinerary, transformItineraryForOverview } from '~/components/Trip/Overview/TripItinerary';
import { TRPC_SETTINGS } from '~/constants/trpc';
import { useTripId } from '~/hooks/useTripId';
import { api } from '~/libs/api';
import type { HeroRouteRecord } from '~/server/trip/getHeroRoutesForTrip';
import type { PlaceImageRecord } from '~/server/trip/getPlaceImagesForTrip';
import type { TripSection } from '~/stores/tpv2/journeyStore';
import { useItineraryScrollProgress } from '../hooks/useItineraryScrollProgress';
import { useTripOverviewSummary } from '../hooks/useTripOverviewSummary';
import { transformOverviewItems } from '../lib/transformOverviewItems';
import { makeHeroRoutesCmsKey, makePlaceImagesCmsKey } from '../overviewPage/HydratedOverview';
import styles from './ItinerarySection.module.css';

export function ItinerarySection({
  id = 'overview-itinerary',
  cardSection = 'overview'
}: Readonly<{ id?: string; cardSection?: TripSection }>) {
  const tripId = useTripId();
  const { isDateUnset, isReadOnly } = useTripOverviewSummary();
  const { data } = api.trip.getOverview.useQuery(
    { tripId },
    { ...TRPC_SETTINGS.QUERY_NO_REFETCH, enabled: !!tripId, keepPreviousData: true }
  );
  const { data: heroRoutesCms } = useQuery<Record<string, HeroRouteRecord>>({
    queryKey: makeHeroRoutesCmsKey(tripId),
    enabled: false
  });
  const { data: placeImagesCms } = useQuery<Record<string, PlaceImageRecord>>({
    queryKey: makePlaceImagesCmsKey(tripId),
    enabled: false
  });

  useItineraryScrollProgress(data?.items, [heroRoutesCms]);

  if (!data) return null;

  const itineraryItems = transformOverviewItems(data.items, heroRoutesCms, { cmsPlaces: placeImagesCms });
  const itemsForSection =
    cardSection === 'accommodation' ? itineraryItems.filter((item) => item.type === 'Stay') : itineraryItems;

  const { groups, originLocationName, returnLegId } = transformItineraryForOverview(itemsForSection);

  return (
    <div id={id} className={styles.itinerarySection}>
      <TripItinerary
        groups={isDateUnset ? groups.map((group) => ({ ...group, dateRange: '' })) : groups}
        originLocationName={originLocationName}
        isEditable={!isReadOnly && !isDateUnset}
        tripId={tripId}
        returnLegId={returnLegId}
        cardSection={cardSection}
        desktopTransparent
      />
    </div>
  );
}
